"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { Map, Table, Calendar, Lock } from "lucide-react";
import Image from "next/image";
import LogoIcon from "@/assets/images/logo.svg";
import { isAuthenticatedSync } from "@/lib/auth";
import { signOutUser } from "@/services/cognito";

export default function PlaygroundBottomNav() {
	const router = useRouter();
	const [isSignedIn, setIsSignedIn] = useState(false);

	// Check authentication status
	useEffect(() => {
		const checkAuth = () => {
			setIsSignedIn(isAuthenticatedSync());
		};
		checkAuth();
		// Check periodically in case auth state changes
		const interval = setInterval(checkAuth, 1000);
		return () => clearInterval(interval);
	}, []);

	const handleSignOut = async () => {
		try {
			await signOutUser();
			setIsSignedIn(false);
			router.push("/playground");
		} catch (error) {
			console.error("Failed to sign out:", error);
		}
	};

	const handleGoToDashboard = () => {
		router.push("/dashboard");
	};

	return (
		<nav
			className="fixed bottom-0 left-0 right-0 bg-[#16251c] text-white py-3 px-6 shadow-2xl flex-shrink-0 z-40 border-t-2 border-[#A7FF5E]/20"
			data-testid="playground-footer"
		>
			<div className="max-w-[1920px] mx-auto flex items-center justify-between">
				{/* Logo and Branding */}
				<div className="flex items-center gap-3">
					<div className="w-10 h-10 bg-[#A7FF5E] rounded-full flex items-center justify-center flex-shrink-0">
						<Image
							src={LogoIcon}
							alt="Tax Lien Vest Logo"
							width={24}
							height={24}
							className="w-6 h-6"
						/>
					</div>
					<div>
						<h2 className="text-base font-extrabold text-[#edffde]">
							Tax Lien Vest
						</h2>
						<p className="text-[#A7FF5E] text-xs">
							{isSignedIn ? "Full Access" : "Playground Mode"}
						</p>
					</div>
				</div>

				{/* Navigation Buttons */}
				<div className="flex items-center gap-3">
					{/* Playground Button */}
					<button
						onClick={() => router.push("/playground")}
						className="px-4 py-2 rounded-lg transition-all flex items-center gap-2 bg-[#A7FF5E] text-[#16251c] font-semibold cursor-pointer"
					>
						<Table className="w-4 h-4" />
						<span className="text-sm">Playground</span>
					</button>

					{/* Full Table Button */}
					<button
						onClick={() => {
							if (isSignedIn) {
								router.push("/dashboard");
							}
						}}
						disabled={!isSignedIn}
						data-testid="full-table-button"
						className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
							isSignedIn
								? "bg-white/10 hover:bg-white/20 text-white cursor-pointer"
								: "bg-white/5 text-white/40 cursor-not-allowed"
						}`}
					>
						{!isSignedIn && <Lock className="w-4 h-4" />}
						<Table className="w-4 h-4" />
						<span className="text-sm">Full Table</span>
					</button>

					{/* Map View Button */}
					<button
						onClick={() => {
							if (isSignedIn) {
								router.push("/dashboard?view=map");
							}
						}}
						disabled={!isSignedIn}
						data-testid="map-view-button"
						className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
							isSignedIn
								? "bg-white/10 hover:bg-white/20 text-white cursor-pointer"
								: "bg-white/5 text-white/40 cursor-not-allowed"
						}`}
					>
						{!isSignedIn && <Lock className="w-4 h-4" />}
						<Map className="w-4 h-4" />
						<span className="text-sm">Map View</span>
					</button>

					{/* Auction Button */}
					<button
						onClick={() => {
							if (isSignedIn) {
								router.push("/dashboard?view=auction");
							}
						}}
						disabled={!isSignedIn}
						data-testid="auction-button"
						className={`px-4 py-2 rounded-lg transition-all flex items-center gap-2 ${
							isSignedIn
								? "bg-white/10 hover:bg-white/20 text-white cursor-pointer"
								: "bg-white/5 text-white/40 cursor-not-allowed"
						}`}
					>
						{!isSignedIn && <Lock className="w-4 h-4" />}
						<Calendar className="w-4 h-4" />
						<span className="text-sm">Auction</span>
					</button>

					{/* Divider */}
					<div className="w-px h-10 bg-white/20 mx-2"></div>

					{/* Auth Button */}
					{isSignedIn ? (
						<button
							onClick={handleSignOut}
							data-testid="playground-signout-button"
							className="px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-all text-sm font-semibold cursor-pointer"
						>
							Sign Out
						</button>
					) : (
						<button
							onClick={() => router.push("/signup")}
							data-testid="footer-signup-button"
							className="px-5 py-2.5 bg-[#A7FF5E] hover:bg-[#cbf7a6] text-[#16251c] rounded-lg transition-all text-sm font-extrabold border-2 border-[#16251c]/20 shadow-lg cursor-pointer"
						>
							Sign Up
						</button>
					)}
				</div>
			</div>
		</nav>
	);
}
